/* eslint-disable no-unused-vars */
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Grid } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import { apiEndpoints, httpMethods } from "../../../constants/api.constants";
import { IMAGE_CONFIG } from "../../../constants/config";
import { loaderPath } from "../../../descriptions/jewellery-inspector/inspectionRequests.description";
import useAPI from "../../../hooks/useAPI";
import useI18N from "../../../hooks/useI18N";
import { addDashboard } from "../../../redux/slices/jewelleryInspector.slices";
import { Eye } from "../../../shared/icon";
import MUIBreadcrumbs from "../../../shared/MUIBreadcrumbs";
import MUIModal from "../../../shared/MUIModal";
import { MUIStyled } from "../../../shared/MUIStyled";
import SABox from "../../../shared/SABox";
import SAButton from "../../../shared/SAButton";
import SACard from "../../../shared/SACard";
import SAImageSlider from "../../../shared/SAContentSlider";
import LazyImage from "../../../shared/SAImageLoader";
import SALoader from "../../../shared/SALoader";
import SAStack from "../../../shared/SAStack";
import SAStatusBadge from "../../../shared/SAStatusBadge";
import SATruncateWithEllipsis from "../../../shared/SATruncateWithEllipsis";
import SATypography from "../../../shared/SATypography";
import SACost from "../../../shared/table-components/SACost";
import SADate from "../../../shared/table-components/SADate";
import SATarget from "../../../shared/table-components/SATarget";
import ProductMaterialsTable from "../../musharka-contracts/ProductMaterialsTable";
import ApproveRejectModal from "./ApproveRejectModal";
import DeliverToShowroomModal from "./DeliverToShowroomModal";
import JewelerSerialNumberTable from "./JewelerSerialNumberTable";
import StartInspectionModal from "./StartInspectionModal";

const DetailsRow = MUIStyled(SABox)({
  display: "flex",
  alignItems: "center",
  background: "#f9fafb",
  borderRadius: 2,
  padding: 8,
  marginBottom: 8,
  gap: 8,
});

const DetailsToggle = MUIStyled(SABox)({
  textTransform: "none",
  fontWeight: 600,
  paddingLeft: 12,
  paddingRight: 12,
  borderRadius: 4,
  paddingTop: 4,
  paddingBottom: 4,
  fontSize: 14,
  boxShadow: "none",
  borderColor: "#6366F1",
  background: "#f3f4f6",
  display: "flex",
  alignItems: "center",
  gap: 8,
});

const DetailsToggleText = MUIStyled(SATypography)({
  color: "#6B7280",
  fontSize: "0.875rem",
  fontWeight: 500,
});

const ModalInfoBox = MUIStyled(SABox)({
  border: "1px solid #e0e0e0",
  padding: 8,
  marginTop: 8,
  display: "flex",
  alignItems: "center",
  color: "#686868",
});

const ProductMaterialsBox = MUIStyled(SABox)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  borderRadius: 4,
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  fontSize: 14,
  boxShadow: "none",
  background: "#f3f4f6",
  display: "flex",
  alignItems: "center",
  gap: 8,
}));

const Row = MUIStyled(SABox)({
  gap: 12,
  display: "flex",
  alignItems: "center",
  backgroundColor: "#f9fafb",
  padding: "8px 12px",
  borderRadius: 8,
  marginBottom: 8,
});

const StyledLabel = MUIStyled(SATypography)({
  color: "#6B7280",
  fontSize: "0.875rem",
  fontWeight: 500,
  whiteSpace: "nowrap",
});

const InspectionDetails = () => {
  // Show more for rejected details
  const [showMoreRejected, setShowMoreRejected] = useState(false);
  const dispatch = useDispatch();
  const { t } = useI18N();
  const { api } = useAPI();
  const location = useLocation();
  const { inspectionId } = useParams();
  const [details, setDetails] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [inspectionStarted, setInspectionStarted] = useState(false);
  const [materialsOpen, setMaterialsOpen] = useState(false);
  const [serialsOpen, setSerialsOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [zoomImg, setZoomImg] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectFiles, setRejectFiles] = useState([]);
  const [designComment, setDesignComment] = useState("");
  const [uploadedImages, setUploadedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [underInspectLoading, setUnderInspectLoading] = useState(false);
  const [completeInspection, setCompleteInspection] = useState(false);
  const [startInspectionLoading, setStartInspectionLoading] = useState(false);

  useEffect(() => {
    getInspectionRequests();
  }, [inspectionId]);

  const getInspectionRequests = async () => {
    setLoading(true);
    const response = await api({
      method: httpMethods.get,
      endPoint: `${apiEndpoints.getInspectionRequests}${inspectionId}/`,
      needLoader: true,
      showToastOnError: true,
    });
    if (response?.status) {
      setDetails(response?.data);
      setInspectionStarted(
        response?.data?.admin_inspection_status !== "PENDING",
      );
      setLoading(false);
    }
  };

  const handleCloseDetailsModal = () => setDetailsModalOpen(false);

  const fetchRequests = async () => {
    const response = await api({
      method: httpMethods.get,
      endPoint: apiEndpoints.getInspectorRequests,
    });
    if (response?.status) dispatch(addDashboard({ data: response?.data }));
  };

  const handleStartInspection = async (status) => {
    setLoading(true);
    const res = await api({
      method: httpMethods.patch,
      endPoint: apiEndpoints.updateAdminStatus(inspectionId),
      needLoader: true,
      loader: loaderPath.inspectionRequests,
      showToastOnError: true,
      data: { admin_inspection_status: status },
    });
    if (res?.status) {
      fetchRequests();
      setInspectionStarted(true);
      setDetails((prev) => ({ ...prev, production_status: "IN_PROGRESS" }));
      getInspectionRequests();
    }
    setLoading(false);
  };

  const handleConfirmation = async (id, status, comment, files = []) => {
    const payload = { admin_inspection_status: status };
    if (status === "REJECTED" && comment) payload.reason = comment;
    let uploadedFilePaths = [];
    if (files.length > 0) {
      const uploadPromises = files.map(async (file) => {
        const path = `inspection-reject-${id}/${Date.now()}-${file.name}`;
        return uploadImageToS3(file, path).then((uploadResponse) => {
          return uploadResponse?.status ? path : null;
        });
      });
      const results = await Promise.all(uploadPromises);
      uploadedFilePaths = results.filter(Boolean);
    }
    if (uploadedFilePaths.length > 0) {
      payload.attachments = uploadedFilePaths;
    }

    const response = await api({
      method: httpMethods.patch,
      endPoint: apiEndpoints.updateInspectionStatus(id),
      needLoader: true,
      showToastOnError: true,
      data: payload,
    });
    if (response?.status) {
      getInspectionRequests();
    }
  };

  // Approve handler
  const handleApprove = async () => {
    setLoading(true);
    await handleConfirmation(selectedProductId, "APPROVED");
    setLoading(false);
    setApproveModalOpen(false);
    // Optionally update details/status
  };

  // Reject handler
  const handleReject = async (data) => {
    setRejectLoading(true);
    await handleConfirmation(
      selectedProductId,
      "REJECTED",
      data?.comment,
      rejectFiles,
    );
    setRejectLoading(false);
    setRejectModalOpen(false);
    setRejectComment("");
    setRejectFiles([]);
  };

  const handleDeliveryStatus = async (status) => {
    setConfirmModalOpen(false);
    setLoading(true);
    const response = await api({
      method: httpMethods.patch,
      endPoint: apiEndpoints.updateDeliveryStatus(inspectionId),
      needLoader: true,
      loader: loaderPath.inspectionRequests,
      showToastOnError: true,
      data: { delivery_status: status },
    });
    if (response?.status) {
      getInspectionRequests();
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(
      (file) =>
        file.type.startsWith("image/") && file.size <= IMAGE_CONFIG.maxSize,
    );
    setUploadedImages(validFiles);
    setImagePreviews(validFiles.map((file) => URL.createObjectURL(file)));
  };

  const uploadImageToS3 = async (file, path) => {
    const payload = {
      bucket_name: process.env.REACT_APP_AWS_BUCKET,
      files: [{ name: path, content_type: file.type }],
    };
    const response = await api({
      method: httpMethods.post,
      endPoint: apiEndpoints.getS3PresignedURL,
      data: payload,
      needLoader: true,
      showToastOnError: true,
    });
    if (response?.status) {
      const awsURL = response?.data?.[path];
      try {
        await fetch(awsURL, { method: "PUT", body: file });
        return { status: true };
      } catch (error) {
        return { status: false, error };
      }
    }
  };

  const handleSaveDesignComment = async (id) => {
    if (!id) return;
    setSaveLoading(true);
    let uploadedImagePaths = [];
    if (uploadedImages.length > 0) {
      const uploadPromises = uploadedImages.map(async (file) => {
        const path = `inspection-comment-${details.id}/${Date.now()}-${file.name}`;
        return uploadImageToS3(file, path).then((uploadResponse) => {
          return uploadResponse?.status ? { file: path } : null;
        });
      });
      const results = await Promise.all(uploadPromises);
      uploadedImagePaths = results.filter(Boolean);
    }
    const payload = {
      comment: designComment,
      jewelry_production_id: details?.id,
      attachments: uploadedImagePaths,
    };
    const response = await api({
      method: httpMethods.patch,
      endPoint: `${apiEndpoints.updateProductComment}${id}/`,
      data: payload,
      needLoader: true,
      showToastOnError: true,
    });
    if (response?.status) {
      setSaveLoading(false);
      getInspectionRequests();
      setUploadedImages([]);
      setImagePreviews([]);
      setDesignComment("");
    }
  };

  if (loading || !details) {
    return (
      <SABox
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <SALoader />
      </SABox>
    );
  }

  const productQuantities =
    details?.manufacturing_request
      ?.manufacturing_product_requested_quantities || [];

  // Check if all products are either approved or rejected
  const allProductsInspected = productQuantities.every(
    (item) => item?.admin_inspection_status !== "PENDING",
  );

  // Calculate total quantity by summing all product quantities
  const total_quantity = productQuantities.reduce(
    (sum, item) => sum + +(item?.quantity || 0),
    0,
  );

  const products = productQuantities.map((item) => {
    const jewelryProduct = item.jewelry_product || {};
    const productMaterials = Array.isArray(jewelryProduct.product_materials)
      ? jewelryProduct.product_materials
      : [];
    const attachments = Array.isArray(
      jewelryProduct.jewelry_product_attachments,
    )
      ? jewelryProduct.jewelry_product_attachments
      : [];

    const manufacturerAttachments = Array.isArray(
      item?.manufactured_product_attachments,
    )
      ? item?.manufactured_product_attachments
      : [];

    return {
      id: jewelryProduct.id ?? null,
      name: jewelryProduct.product_name ?? "-",
      type: jewelryProduct.product_type ?? "-",
      quantity: item.quantity ?? 0,
      weight: jewelryProduct.weight ?? "-",
      description: jewelryProduct?.description ?? "-",
      design_images: attachments.map((att) => att?.url?.url).filter(Boolean),
      manufacturerAttachments: manufacturerAttachments
        ?.map?.((att) => att?.file?.url)
        .filter(Boolean),
      product_materials: productMaterials,
    };
  });

  const imageList = products.map((item) => ({
    url: item.design_images?.[0],
  }));
  const currentProduct = products[selectedIndex] || {};

  const {
    admin_inspection_status,
    started_at,
    completed_at,
    delivery_date,
    delivery_status,
    payment,
    is_payment_completed,
    manufacturing_request,
    // material_delivery_status,
  } = details;

  function getProductionStatusLabel(status, t) {
    switch (status) {
      case "PENDING":
        return t("start-product-inspection");
      case "IN_PROGRESS":
        return t("under-inspect");
      case "ADMIN_APPROVAL":
      case "COMPLETED":
        return t("completed");

      default:
        return t("completed");
    }
  }

  const { isTaqabeth } = location.state || {};

  // const recentTab = location.state && location.state.activeTab;
  // const recentRoute = location.state && location.state.activeRoute;

  // const homeRoute =
  //   (location.state && location.state.homeRoute) || "/inspection-requests";
  // const label = location.state && location.state.label;
  // const href = location.state && location.state.href;

  // const inspectionHref =
  //   recentTab && recentRoute
  //     ? `${recentRoute}?tab=${recentTab}`
  //     : `/inspection-requests?tab=${recentTab}`;

  const {
    activeTab: recentTab,
    activeRoute: recentRoute,
    homeRoute = "/inspection-requests",
    label,
    href,
  } = location.state || {};

  const inspectionHref =
    recentTab && recentRoute
      ? `${recentRoute}?tab=${recentTab}`
      : `/inspection-requests${recentTab ? `?tab=${recentTab}` : ""}`;

  const breadcrumbsData = [
    { label: "home", href: homeRoute },
    {
      label:
        label ||
        `${recentRoute ? "manufacturing-requests" : "inspection-requests"}`,
      href: href || inspectionHref,
    },
    { label: inspectionId, noTranslate: true },
  ];

  return (
    <>
      <SAStack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <SAStack>
          <MUIBreadcrumbs breadcrumbsData={breadcrumbsData} />
        </SAStack>
      </SAStack>
      <SACard>
        <SABox sx={{ background: "#f5f5f5", p: 2, mb: 2, borderRadius: 2 }}>
          <SAStack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <SABox>
              <SATypography fontWeight={600}>
                {t("requested-quantity")}
              </SATypography>
              <SATypography>
                {+productQuantities[selectedIndex]?.quantity} {t("out-of")}{" "}
                {total_quantity} {t("pieces")}
              </SATypography>
            </SABox>
            {!isTaqabeth && (
              <SABox>
                {is_payment_completed ? (
                  <SAButton
                    variant="contained"
                    color="primary"
                    onClick={() => setConfirmModalOpen("deliver")}
                    disabled={
                      // admin_inspection_status !== "COMPLETED" ||
                      delivery_status === "DELIVERED"
                    }
                  >
                    {delivery_status === "OUT_FOR_DELIVERY"
                      ? t("out-for-delivery")
                      : delivery_status === "DELIVERED"
                        ? t("delivered")
                        : t("deliver-to-showroom")}
                  </SAButton>
                ) : (
                  <SAButton
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      if (admin_inspection_status === "IN_PROGRESS") {
                        setCompleteInspection(true);
                      } else {
                        setConfirmModalOpen("start");
                      }
                    }}
                    disabled={
                      (inspectionStarted &&
                        admin_inspection_status !== "IN_PROGRESS") ||
                      (admin_inspection_status === "IN_PROGRESS" &&
                        !allProductsInspected)
                    }
                  >
                    {getProductionStatusLabel(admin_inspection_status, t)}
                  </SAButton>
                )}
              </SABox>
            )}
          </SAStack>
        </SABox>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <SATypography
              variant="subtitle1"
              fontSize={16}
              fontWeight={700}
              gutterBottom
              mt={2}
            >
              {t("manufacture-details")}
            </SATypography>

            <DetailsRow>
              <DetailsToggleText>
                {t("payment-status")}
                {":"}
              </DetailsToggleText>

              <SATypography>
                <SAStatusBadge
                  status={is_payment_completed ? "COMPLETED" : "PENDING"}
                />
              </SATypography>
            </DetailsRow>

            <DetailsRow>
              <DetailsToggleText>
                {t("delivery-status")}
                {":"}
              </DetailsToggleText>

              <SATypography>
                <SAStatusBadge status={delivery_status} />
              </SATypography>
            </DetailsRow>

            {/* <DetailsRow>
              <DetailsToggleText>
                {t("material-delivery-status")}
                {":"}
              </DetailsToggleText>

              <SATypography>
                <SAStatusBadge status={material_delivery_status} />
              </SATypography>
            </DetailsRow> */}

            <DetailsRow>
              <DetailsToggleText>
                {t("delivery-date")}
                {":"}
              </DetailsToggleText>

              <SATypography>
                <SADate date={delivery_date} />
              </SATypography>
            </DetailsRow>
          </Grid>

          <Grid item xs={12} md={6}>
            <SATypography
              variant="subtitle1"
              fontSize={16}
              fontWeight={700}
              gutterBottom
              mt={2}
            >
              {t("timeline")}
            </SATypography>

            <DetailsRow>
              <DetailsToggleText>
                {t("started-date")}
                {":"}
              </DetailsToggleText>

              <SATypography>
                <SADate date={started_at} />
              </SATypography>
            </DetailsRow>

            <DetailsRow>
              <DetailsToggleText>
                {t("completed-date")}
                {":"}
              </DetailsToggleText>

              <SATypography>
                <SADate date={completed_at} />
              </SATypography>
            </DetailsRow>
          </Grid>

          <Grid item xs={12} md={6}>
            <SATypography
              variant="subtitle1"
              fontSize={16}
              fontWeight={700}
              gutterBottom
              mt={2}
            >
              {t("participant-details")}
            </SATypography>

            <DetailsRow>
              <DetailsToggleText>
                {t("jeweler")}
                {":"}
              </DetailsToggleText>

              <SATruncateWithEllipsis
                text={
                  manufacturing_request?.business?.name ||
                  manufacturing_request?.created_by?.name
                }
                maxLength={40}
              />
            </DetailsRow>
            <DetailsRow>
              <DetailsToggleText>
                {t("manufacturer")}
                {":"}
              </DetailsToggleText>

              <SATruncateWithEllipsis
                text={
                  manufacturing_request?.manufacturing_estimation?.created_by
                    ?.name
                }
                maxLength={40}
              />
            </DetailsRow>
          </Grid>

          {payment && (
            <Grid item xs={12} md={6}>
              <SATypography
                variant="subtitle1"
                fontSize={16}
                fontWeight={700}
                gutterBottom
              >
                {t("payment-details")}
              </SATypography>
              <DetailsRow>
                <DetailsToggleText>
                  {t("payment-type")}
                  {":"}
                </DetailsToggleText>

                <SATypography>{payment?.payment_type}</SATypography>
              </DetailsRow>
              <DetailsRow>
                <DetailsToggleText>
                  {t("total-amount")}
                  {":"}
                </DetailsToggleText>

                <SATypography>
                  <SACost value={payment?.total_amount} />
                </SATypography>
              </DetailsRow>

              {payment?.payment_type === "MUSHARAKAH" && (
                <Row
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSerialsOpen((prev) => !prev)}
                >
                  <StyledLabel variant="body2">{`${t("jeweler-selected-serial-number")}:`}</StyledLabel>
                  <ProductMaterialsBox>
                    <SATypography
                      sx={{
                        color: "#111827",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      {t(serialsOpen ? "hide" : "view")}
                    </SATypography>
                    <ExpandMoreIcon
                      style={{
                        transform: serialsOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s",
                      }}
                    />
                  </ProductMaterialsBox>
                </Row>
              )}
              <Collapse in={serialsOpen} timeout="auto" unmountOnExit>
                <SABox sx={{ mt: 1 }}>
                  <JewelerSerialNumberTable
                    data={payment?.asset_serial_numbers}
                    t={t}
                  />
                </SABox>
              </Collapse>
            </Grid>
          )}
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SATypography
              variant="subtitle1"
              fontSize={16}
              fontWeight={700}
              color="primary.main"
              sx={{ mt: 2 }}
            >
              {t("product-details")}
            </SATypography>
          </Grid>

          <Grid item xs={12} md={4}>
            <SAImageSlider images={imageList} onChange={setSelectedIndex} />
          </Grid>

          <Grid item xs={12} md={8}>
            <DetailsRow>
              <DetailsToggleText>
                {t("status")}
                {":"}
              </DetailsToggleText>
              <SATypography>
                {(
                  <SAStatusBadge
                    status={
                      productQuantities[selectedIndex]?.admin_inspection_status
                    }
                  />
                ) || "-"}
              </SATypography>
            </DetailsRow>
            <DetailsRow>
              <DetailsToggleText>{t("product-details")}:</DetailsToggleText>
              <SATypography
                variant="text"
                onClick={() => setDetailsModalOpen(true)}
                sx={{
                  minWidth: 0,
                  background: "#f3f4f6",
                  fontSize: 14,
                  px: 1.5,
                  fontWeight: 500,
                  py: 0.5,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {t("view-details")}
                <Eye />
              </SATypography>
            </DetailsRow>
            <DetailsRow
              style={{ cursor: "pointer" }}
              onClick={() => setMaterialsOpen((prev) => !prev)}
            >
              <DetailsToggleText>{t("product-materials")}:</DetailsToggleText>
              <DetailsToggle>
                <DetailsToggleText>
                  {t(materialsOpen ? "hide" : "view")}
                </DetailsToggleText>
                <ExpandMoreIcon
                  style={{
                    transform: materialsOpen
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              </DetailsToggle>
            </DetailsRow>
            <Collapse in={materialsOpen} timeout="auto" unmountOnExit>
              <SABox sx={{ mt: 0.5 }}>
                <ProductMaterialsTable product={currentProduct} t={t} />
              </SABox>
            </Collapse>
          </Grid>
        </Grid>
        {/* Product Details Modal */}
        <MUIModal
          open={detailsModalOpen}
          maxWidth="sm"
          handleClose={handleCloseDetailsModal}
          content={
            <SABox p={3}>
              <SABox
                sx={{
                  background: "#f5f5f5",
                  p: 1,
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <SABox
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <SATypography fontWeight={600}>
                    {t("requested-quantity")}
                    {" : "}
                  </SATypography>
                  <SATypography ml={1}>
                    {+products[selectedIndex]?.quantity} {t("pieces")}
                  </SATypography>
                </SABox>
                {!isTaqabeth && (
                  <SAStack direction="row" gap={2}>
                    {productQuantities[selectedIndex]
                      ?.admin_inspection_status === "APPROVED" ? (
                      <SABox
                        sx={{
                          minWidth: 80,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          px: 1,
                          py: 0.5,
                          gap: 1,
                          border: "1px solid #9e9a93",
                        }}
                      >
                        <CheckOutlinedIcon /> {t("approved")}
                      </SABox>
                    ) : productQuantities[selectedIndex]
                        ?.admin_inspection_status === "REJECTED" ? (
                      <SAButton
                        variant="outlined"
                        onClick={() => {
                          setSelectedProductId(
                            productQuantities[selectedIndex]?.id,
                          );
                          setRejectModalOpen(true);
                        }}
                        disabled={admin_inspection_status === "PENDING"}
                        loading={rejectLoading}
                      >
                        {t("reject")}
                      </SAButton>
                    ) : (
                      <>
                        <SAButton
                          variant="contained"
                          onClick={() => {
                            setSelectedProductId(
                              productQuantities[selectedIndex]?.id,
                            );
                            setApproveModalOpen(true);
                          }}
                          disabled={admin_inspection_status === "PENDING"}
                          loading={saveLoading}
                        >
                          {t("approve")}
                        </SAButton>
                        <SAButton
                          variant="outlined"
                          onClick={() => {
                            setSelectedProductId(
                              productQuantities[selectedIndex]?.id,
                            );
                            setRejectModalOpen(true);
                          }}
                          disabled={admin_inspection_status === "PENDING"}
                          loading={rejectLoading}
                        >
                          {t("reject")}
                        </SAButton>
                      </>
                    )}
                  </SAStack>
                )}
              </SABox>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <SATypography fontWeight={600}>
                    {t("product-name")}
                  </SATypography>
                  <ModalInfoBox>
                    <SATruncateWithEllipsis
                      text={products[selectedIndex]?.name}
                      maxLength={65}
                    />
                  </ModalInfoBox>
                </Grid>
                <Grid item xs={6}>
                  <SATypography fontWeight={600}>
                    {t("product-type")}
                  </SATypography>
                  <ModalInfoBox>
                    <SATypography>{products[selectedIndex]?.type}</SATypography>
                  </ModalInfoBox>
                </Grid>
                <Grid item xs={6}>
                  <SATypography fontWeight={600}>
                    {t("product-weight")}
                  </SATypography>
                  <ModalInfoBox>
                    <SATarget value={products[selectedIndex]?.weight} />
                  </ModalInfoBox>
                </Grid>
                <Grid item xs={12}>
                  <SATypography fontWeight={600}>
                    {t("description")}
                  </SATypography>
                  <ModalInfoBox>
                    <SATruncateWithEllipsis
                      text={products[selectedIndex]?.description}
                      maxLength={65}
                    />
                  </ModalInfoBox>
                </Grid>
                <Grid item xs={12}>
                  <SATypography fontWeight={600}>
                    {t("design-sample")}
                  </SATypography>
                  <SAStack
                    direction="row"
                    gap={2}
                    mt={1}
                    sx={{
                      width: "100%",
                      overflowX: "auto",
                      pb: 1,
                    }}
                  >
                    {(products[selectedIndex]?.design_images || []).map(
                      (img, i) => (
                        <SABox
                          key={i}
                          sx={{
                            width: 100,
                            height: 100,
                            background: "#bbb",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                            borderRadius: 2,
                            flex: "0 0 auto",
                            cursor: "zoom-in",
                          }}
                          onClick={() => setZoomImg(img)}
                        >
                          {img && (
                            <img
                              src={img}
                              alt={`design-${i}`}
                              style={{
                                width: 80,
                                height: 80,
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          )}
                        </SABox>
                      ),
                    )}
                  </SAStack>
                </Grid>
                <Grid item xs={12}>
                  <SATypography fontWeight={600}>
                    {t("manufacturer-design-sample")}
                  </SATypography>
                  <SAStack
                    direction="row"
                    gap={2}
                    mt={1}
                    sx={{
                      width: "100%",
                      overflowX: "auto",
                      pb: 1,
                    }}
                  >
                    {(
                      products[selectedIndex]?.manufacturerAttachments || []
                    )?.map((img, i) => (
                      <SABox
                        key={i}
                        sx={{
                          width: 100,
                          height: 100,
                          background: "#bbb",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          borderRadius: 2,
                          flex: "0 0 auto",
                          cursor: "zoom-in",
                        }}
                        onClick={() => setZoomImg(img)}
                      >
                        {img && (
                          <img
                            src={img}
                            alt={`design-${i}`}
                            style={{
                              width: 80,
                              height: 80,
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                        )}
                      </SABox>
                    ))}
                  </SAStack>
                </Grid>
              </Grid>

              {!!productQuantities[selectedIndex]?.rejected_inspected_products
                ?.length && (
                <>
                  {/* Show first rejected detail always */}
                  <SAStack>
                    <SABox>
                      <SATypography
                        sx={{ mt: 1, fontSize: 18, fontWeight: 600 }}
                      >
                        {t("rejection-details")}
                      </SATypography>
                    </SABox>
                    <SABox>
                      <SABox
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                          mt: 1,
                        }}
                      >
                        <SATypography>
                          {t("comment")}
                          {":"}
                        </SATypography>
                        <SABox
                          sx={{
                            border: "1px solid #e0e0e0",
                            padding: "5px",
                            display: "flex",
                            width: "100%",
                            alignItems: "center",
                            color: "#686868",
                          }}
                        >
                          <SATruncateWithEllipsis
                            text={
                              productQuantities[selectedIndex]
                                ?.rejected_inspected_products[0]?.reason
                            }
                            maxLength={60}
                          />
                        </SABox>
                      </SABox>
                      <SAStack
                        direction="row"
                        spacing={1}
                        mt={2}
                        sx={{ overflowX: "auto", maxWidth: 550 }}
                      >
                        {productQuantities[selectedIndex]
                          ?.rejected_inspected_products[0]
                          ?.inspection_rejection_attachments?.length &&
                          productQuantities[
                            selectedIndex
                          ]?.rejected_inspected_products[0]?.inspection_rejection_attachments?.map(
                            (src, idx) => (
                              <SABox
                                key={idx}
                                sx={{
                                  width: 80,
                                  height: 80,
                                  borderRadius: 2,
                                  overflow: "hidden",
                                  border: "1px solid #e0e0e0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  position: "relative",
                                  mr: 1,
                                  flex: "0 0 auto",
                                }}
                              >
                                <LazyImage
                                  src={src?.file?.url}
                                  className="main-img"
                                  previewImage={true}
                                />
                              </SABox>
                            ),
                          )}
                      </SAStack>
                    </SABox>
                  </SAStack>
                  {/* Show More button */}
                  {productQuantities[selectedIndex]?.rejected_inspected_products
                    ?.length > 1 && (
                    <SABox mt={2} textAlign="center">
                      <SAButton
                        variant="text"
                        onClick={() => setShowMoreRejected((prev) => !prev)}
                      >
                        {showMoreRejected ? t("show-less") : t("show-more")}
                      </SAButton>
                    </SABox>
                  )}
                  {/* Collapsible section for other rejected details */}
                  {showMoreRejected && (
                    <SABox mt={2}>
                      {productQuantities[
                        selectedIndex
                      ]?.rejected_inspected_products
                        .slice(1)
                        .map((rejectedDetails, index) => (
                          <>
                            <SABox key={index}>
                              <SABox>
                                <SATypography>
                                  {t("rejection-details")}
                                </SATypography>
                              </SABox>
                              <SABox>
                                <SABox
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                    mt: 1,
                                  }}
                                >
                                  <SATypography>
                                    {t("comment")}
                                    {":"}
                                  </SATypography>
                                  <SABox
                                    sx={{
                                      border: "1px solid #e0e0e0",
                                      padding: "5px",
                                      display: "flex",
                                      width: "100%",
                                      alignItems: "center",
                                      color: "#686868",
                                    }}
                                  >
                                    <SATruncateWithEllipsis
                                      text={rejectedDetails?.reason}
                                      maxLength={60}
                                    />
                                  </SABox>
                                </SABox>
                                <SAStack
                                  direction="row"
                                  spacing={1}
                                  mt={2}
                                  sx={{ overflowX: "auto", maxWidth: 550 }}
                                >
                                  {rejectedDetails
                                    ?.inspection_rejection_attachments
                                    ?.length &&
                                    rejectedDetails?.inspection_rejection_attachments?.map(
                                      (src, idx) => (
                                        <SABox
                                          key={idx}
                                          sx={{
                                            width: 80,
                                            height: 80,
                                            borderRadius: 2,
                                            overflow: "hidden",
                                            border: "1px solid #e0e0e0",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            position: "relative",
                                            mr: 1,
                                            flex: "0 0 auto",
                                          }}
                                        >
                                          <LazyImage
                                            src={src?.file?.url}
                                            className="main-img"
                                            previewImage={true}
                                          />
                                        </SABox>
                                      ),
                                    )}
                                </SAStack>
                              </SABox>
                            </SABox>
                            {/* Divider between each rejected detail except last */}
                            {index <
                              productQuantities[selectedIndex]
                                ?.rejected_inspected_products.length -
                                2 && (
                              <SABox
                                my={2}
                                sx={{ borderBottom: "1px solid #e0e0e0" }}
                              />
                            )}
                          </>
                        ))}
                    </SABox>
                  )}
                </>
              )}

              {/* Show comment and upload design field if delivered to showroom */}
              {/* {delivery_status === "DELIVERED" && (
                <>
                  {productQuantities[selectedIndex]?.comment && (
                    <SABox sx={{ marginTop: 2 }}>
                      <SATypography fontWeight={600}>
                        {t("latest-comment")}
                      </SATypography>
                      <ModalInfoBox>
                        <SATruncateWithEllipsis
                          text={productQuantities[selectedIndex]?.comment}
                          maxLength={65}
                        />
                      </ModalInfoBox>
                    </SABox>
                  )}
                  <SABox sx={{ mt: 3 }}>
                    <SATypography
                      sx={{ fontWeight: 600, fontSize: 15, mb: 0.5 }}
                    >
                      {t("comment")}
                    </SATypography>
                    <textarea
                      style={{
                        width: "100%",
                        minHeight: 60,
                        borderRadius: 8,
                        border: "1px solid #ccc",
                        padding: 8,
                      }}
                      value={designComment}
                      onChange={(e) => setDesignComment(e.target.value)}
                      placeholder={t("write-comment-here")}
                    />
                    <SATypography
                      sx={{ fontWeight: 600, fontSize: 15, mb: 0.5, mt: 2 }}
                    >
                      {t("manufactured-design")}
                    </SATypography>
                    <SABox mb={2}>
                      <SAButton variant="outlined" component="label">
                        <FileUploadOutlinedIcon sx={{ mr: 2 }} />
                        {t("upload-design-sample")}
                        <input
                          type="file"
                          hidden
                          multiple
                          accept={IMAGE_CONFIG.acceptedTypes}
                          onChange={handleImageChange}
                        />
                      </SAButton>
                      <SAStack
                        direction="row"
                        spacing={1}
                        mt={2}
                        sx={{ overflowX: "auto", maxWidth: 550 }}
                      >
                        {imagePreviews.map((src, idx) => (
                          <SABox
                            key={idx}
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: 2,
                              overflow: "hidden",
                              border: "1px solid #e0e0e0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              position: "relative",
                              mr: 1,
                              flex: "0 0 auto",
                            }}
                          >
                            <img
                              src={src}
                              alt={`preview-${idx}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                            <SATypography
                              size="small"
                              sx={{
                                position: "absolute",
                                top: 2,
                                right: 2,
                                minWidth: 0,
                                width: 22,
                                height: 22,
                                borderRadius: "50%",
                                zIndex: 2,
                                cursor: "pointer",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                              onClick={() => {
                                setUploadedImages((prev) =>
                                  prev.filter((_, i) => i !== idx),
                                );
                                setImagePreviews((prev) =>
                                  prev.filter((_, i) => i !== idx),
                                );
                              }}
                            >
                              <CloseOutlinedIcon fontSize="small" />
                            </SATypography>
                          </SABox>
                        ))}
                      </SAStack>
                    </SABox>
                    <SABox mt={4} textAlign="center">
                      <SAButton
                        onClick={() => {
                          setDesignComment("");
                          setUploadedImages([]);
                          setImagePreviews([]);
                          setDetailsModalOpen(false);
                        }}
                        sx={{ mr: 2 }}
                        disabled={saveLoading}
                      >
                        {t("cancel")}
                      </SAButton>
                      <SAButton
                        color="primary"
                        variant="contained"
                        onClick={() =>
                          handleSaveDesignComment(
                            productQuantities[selectedIndex]?.id,
                          )
                        }
                        loading={saveLoading ? 1 : 0}
                      >
                        {t("save-updates")}
                      </SAButton>
                    </SABox>
                  </SABox>
                </>
              )} */}
            </SABox>
          }
        />
        {/* Reject Modal */}
        <ApproveRejectModal
          open={rejectModalOpen}
          onClose={() => setRejectModalOpen(false)}
          onSend={handleReject}
          loading={rejectLoading}
          approveModalOpen={approveModalOpen}
          setApproveModalOpen={setApproveModalOpen}
          handleApprove={handleApprove}
          comment={rejectComment}
          setComment={setRejectComment}
          rejectFiles={rejectFiles}
          setRejectFiles={setRejectFiles}
        />

        {/* Deliver To Showroom / Out For Delivery Modal */}
        <DeliverToShowroomModal
          open={confirmModalOpen === "deliver"}
          onClose={() => setConfirmModalOpen(false)}
          onConfirm={() =>
            handleDeliveryStatus(
              delivery_status === "OUT_FOR_DELIVERY"
                ? "DELIVERED"
                : "OUT_FOR_DELIVERY",
            )
          }
          t={t}
          loading={loading}
          deliveryStatus={
            delivery_status === "OUT_FOR_DELIVERY"
              ? "OUT_FOR_DELIVERY"
              : "PENDING"
          }
        />
        {/* Start Inspection Modal */}
        <StartInspectionModal
          open={confirmModalOpen === "start"}
          onClose={() => setConfirmModalOpen(false)}
          onConfirm={async () => {
            setConfirmModalOpen(false);
            setStartInspectionLoading(true);
            await handleStartInspection("IN_PROGRESS");
            setStartInspectionLoading(false);
          }}
          t={t}
          loading={startInspectionLoading}
          text={t("start-inspection-confirmation")}
        />
        {/* Complete Inspection Modal */}
        <StartInspectionModal
          open={completeInspection}
          onClose={() => setCompleteInspection(false)}
          onConfirm={async () => {
            setCompleteInspection(false);
            setUnderInspectLoading(true);
            await handleStartInspection("COMPLETED");
            setUnderInspectLoading(false);
          }}
          t={t}
          loading={underInspectLoading}
          text={t("under-inspect-confirm-message")}
          disabled={!allProductsInspected}
        />
        {/* Image Zoom Modal */}
        <MUIModal
          open={!!zoomImg}
          maxWidth={500}
          handleClose={() => setZoomImg(null)}
          content={
            zoomImg && (
              <SABox
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 2,
                }}
              >
                <img
                  src={zoomImg}
                  alt="zoomed-design"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                  }}
                />
              </SABox>
            )
          }
        />
      </SACard>
    </>
  );
};

export default InspectionDetails;
